"use client";

import useSWR, { mutate as globalMutate } from "swr";
import { useAuth } from "@clerk/nextjs";
import { useCallback } from "react";
import { createFetcher } from "@/lib/api";
import type {
  ValidateExprResponse,
  Workflow,
  WorkflowInput,
  WorkflowsResponse,
} from "@/lib/api-types";

const LIST_KEY = "/api/v1/workflows";

export function useWorkflows() {
  const { getToken } = useAuth();
  const { data, error, isLoading } = useSWR<WorkflowsResponse>(
    LIST_KEY,
    createFetcher(getToken),
  );
  return {
    workflows: data?.workflows ?? [],
    isLoading,
    error,
  };
}

async function authHeaders(getToken: () => Promise<string | null>): Promise<HeadersInit> {
  const token = await getToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function parseError(res: Response, fallback: string): Promise<never> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  throw new Error(body.error ?? `${fallback} (${res.status})`);
}

export function useWorkflowMutations() {
  const { getToken } = useAuth();

  const create = useCallback(
    async (input: WorkflowInput): Promise<Workflow> => {
      const res = await fetch(LIST_KEY, {
        method: "POST",
        headers: await authHeaders(getToken),
        body: JSON.stringify(input),
      });
      if (!res.ok) await parseError(res, "Failed to create workflow");
      await globalMutate(LIST_KEY);
      return res.json();
    },
    [getToken],
  );

  const update = useCallback(
    async (id: string, input: WorkflowInput): Promise<Workflow> => {
      const res = await fetch(`${LIST_KEY}/${id}`, {
        method: "PUT",
        headers: await authHeaders(getToken),
        body: JSON.stringify(input),
      });
      if (!res.ok) await parseError(res, "Failed to update workflow");
      await globalMutate(LIST_KEY);
      return res.json();
    },
    [getToken],
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      const res = await fetch(`${LIST_KEY}/${id}`, {
        method: "DELETE",
        headers: await authHeaders(getToken),
      });
      if (!res.ok && res.status !== 204) {
        await parseError(res, "Failed to delete workflow");
      }
      await globalMutate(LIST_KEY);
    },
    [getToken],
  );

  const reorder = useCallback(
    async (orderedIDs: string[]): Promise<Workflow[]> => {
      const res = await fetch(`${LIST_KEY}/reorder`, {
        method: "PUT",
        headers: await authHeaders(getToken),
        body: JSON.stringify({ order: orderedIDs }),
      });
      if (!res.ok) await parseError(res, "Failed to reorder workflows");
      await globalMutate(LIST_KEY);
      const body = (await res.json()) as WorkflowsResponse;
      return body.workflows;
    },
    [getToken],
  );

  // validateExpr surfaces CEL compile errors inline in the advanced
  // editor. We POST to the dedicated endpoint instead of the full
  // workflow PUT so the user can sanity-check expressions without
  // having to fill in a name + strategy.
  const validateExpr = useCallback(
    async (expr: string): Promise<ValidateExprResponse> => {
      const res = await fetch(`${LIST_KEY}/validate`, {
        method: "POST",
        headers: await authHeaders(getToken),
        body: JSON.stringify({ expr }),
      });
      // The endpoint always returns 200 with {ok, error?}; only network
      // failures throw here.
      return res.json();
    },
    [getToken],
  );

  return { create, update, remove, reorder, validateExpr };
}
